// What Help's player (VideoPlayer) reads from one walkthrough's scenes: the
// scene playing at a moment, the caption to show then, a time as 1:05, and
// the same captions as WebVTT for a phone's own full screen player.

import type { Walkthrough } from './shelf';

/** One scene as the player reads it: seconds from the start, and its caption. */
export interface PlayerScene {
	at: number;
	end: number;
	caption: string;
	place: 'bottom' | 'top';
}

export function playerScenes(w: Walkthrough): PlayerScene[] {
	return w.scenes.map((s) => ({
		at: s.startMs / 1000,
		end: s.endMs / 1000,
		caption: s.caption,
		place: s.place,
	}));
}

/**
 * Which recording a link plays: its address without the signature. A new
 * week's run puts its videos under another address, so a fresh link to the
 * same recording keeps it, and a link to a new one does not.
 */
export function recordingOf(link: string | undefined): string {
	return (link ?? '').split('?')[0] ?? '';
}

/** A length or a moment as minutes and seconds, 1:05, whole seconds down. */
export function clock(seconds: number): string {
	const s = Math.max(0, Math.floor(seconds));
	return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** The scene playing at a moment: the last one started, or the first before any has. */
export function sceneAt(scenes: PlayerScene[], seconds: number): number {
	let index = 0;
	scenes.forEach((s, i) => {
		if (seconds >= s.at) index = i;
	});
	return index;
}

/**
 * The scene whose caption shows at a moment: the scene playing, as the design's
 * player shows it, so the first caption is up before the video starts.
 */
export function captionAt(scenes: PlayerScene[], seconds: number): PlayerScene | null {
	const s = scenes[sceneAt(scenes, seconds)];
	return s && s.caption.trim() ? s : null;
}

function vttTime(seconds: number): string {
	const ms = Math.max(0, Math.round(seconds * 1000));
	const pad = (n: number, width = 2) => String(n).padStart(width, '0');
	const h = Math.floor(ms / 3_600_000);
	const m = Math.floor(ms / 60_000) % 60;
	const s = Math.floor(ms / 1000) % 60;
	return `${pad(h)}:${pad(m)}:${pad(s)}.${pad(ms % 1000, 3)}`;
}

/**
 * The captions as WebVTT. A phone's own full screen player hides everything
 * drawn over the video, so the player hands it these to show instead. A
 * caption the scene puts at the top stays at the top there too.
 */
export function vttOf(scenes: PlayerScene[]): string {
	const cues = scenes
		.filter((s) => s.caption.trim() && s.end > s.at)
		.map((s, i) => {
			const settings = s.place === 'top' ? ' line:0' : '';
			// A line break ends a cue's text and an arrow starts a cue's
			// timing, so neither may stand in a caption; & and < start
			// WebVTT's entities and tags, so they are written as entities.
			const text = s.caption
				.replace(/\s*\n\s*/g, ' ')
				.replace(/-->/g, '→')
				.replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;');
			return `${i + 1}\n${vttTime(s.at)} --> ${vttTime(s.end)}${settings}\n${text}`;
		});
	return ['WEBVTT', ...cues].join('\n\n') + '\n';
}
